/**
 * InvestigationDiffPage — compare two investigations side-by-side to
 * spot recurring drivers or diverging root causes. Route: `/investigations/diff?a=<id>&b=<id>`
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, GitCompareArrows, CheckCircle2, Layers, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api';

function VerdictColumn({ inv, label, testIdPrefix }) {
  if (!inv) return null;
  const v = inv.verdict || {};
  return (
    <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-3" data-testid={`${testIdPrefix}-column`}>
      <div className="flex items-center gap-2">
        <Badge className="text-[10px] bg-indigo-500/10 border border-indigo-500/30 text-indigo-300">{label}</Badge>
        <span className="text-[11px] text-muted-foreground font-mono truncate">{inv.investigation_id}</span>
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2">{inv.question}</p>
      <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
        <span>{(inv.target || {}).metric}</span>
        <span>{inv.status}</span>
        <span>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</span>
      </div>
      <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
          <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Verdict</span>
        </div>
        <p className="text-xs text-foreground/95">{v.verdict || '(no summary)'}</p>
      </div>
      {(v.top_drivers || []).length > 0 && (
        <ol className="space-y-1">
          {v.top_drivers.map((d, i) => (
            <li key={i} className="text-[11px] text-foreground/95 flex items-start gap-1.5">
              <span className="text-muted-foreground font-mono">{i + 1}.</span>
              <span className="flex-1">{d.statement}</span>
              {typeof d.confidence === 'number' && (
                <span className="text-[9px] text-muted-foreground font-mono">{Math.round(d.confidence * 100)}%</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function InvestigationDiffPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const a = params.get('a');
  const b = params.get('b');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!a || !b) { setLoading(false); return; }
    api.diffInvestigations(a, b)
      .then(setData)
      .catch((err) => toast.error(err.message || 'Failed to load diff'))
      .finally(() => setLoading(false));
  }, [a, b]);

  if (!a || !b) {
    return (
      <div className="p-6" data-testid="diff-missing-ids">
        <p className="text-sm text-muted-foreground">Both investigation IDs are required (query params ?a=&b=).</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/investigations')} className="mt-3">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to Investigations
        </Button>
      </div>
    );
  }
  if (loading) {
    return <div className="p-6"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>;
  }
  if (!data) return null;

  const d = data.diff || {};

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="investigation-diff-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-indigo-400" /> Investigation Diff
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Side-by-side comparison of two investigations — spot recurring drivers or diverging conclusions.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/investigations')} data-testid="diff-back-link">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <VerdictColumn inv={data.a} label="A" testIdPrefix="diff-a" />
        <VerdictColumn inv={data.b} label="B" testIdPrefix="diff-b" />
      </div>

      {/* Shared drivers */}
      <div className="rounded-xl border border-border/50 bg-card/40 p-4" data-testid="diff-shared-drivers">
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-semibold text-foreground">Recurring drivers (in both)</span>
        </div>
        {(d.shared_drivers || []).length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No drivers matched across the two investigations.</p>
        ) : (
          <ul className="space-y-1 list-disc list-inside">
            {d.shared_drivers.map((row, i) => (
              <li key={i} className="text-xs text-emerald-300" data-testid={`diff-shared-${i}`}>{row.statement}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Unique */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border/50 bg-card/40 p-4" data-testid="diff-unique-a">
          <div className="text-xs font-semibold text-foreground mb-2">Only in A</div>
          {(d.unique_to_a || []).length === 0 ? <p className="text-[11px] text-muted-foreground">—</p> : (
            <ul className="space-y-1 list-disc list-inside">
              {d.unique_to_a.map((r, i) => <li key={i} className="text-[11px] text-foreground/90">{r.statement}</li>)}
            </ul>
          )}
        </div>
        <div className="rounded-xl border border-border/50 bg-card/40 p-4" data-testid="diff-unique-b">
          <div className="text-xs font-semibold text-foreground mb-2">Only in B</div>
          {(d.unique_to_b || []).length === 0 ? <p className="text-[11px] text-muted-foreground">—</p> : (
            <ul className="space-y-1 list-disc list-inside">
              {d.unique_to_b.map((r, i) => <li key={i} className="text-[11px] text-foreground/90">{r.statement}</li>)}
            </ul>
          )}
        </div>
      </div>

      {/* Module overlap */}
      <div className="rounded-xl border border-border/50 bg-card/40 p-4" data-testid="diff-modules">
        <div className="flex items-center gap-1.5 mb-2">
          <Layers className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold text-foreground">Modules used</span>
        </div>
        <div className="grid md:grid-cols-3 gap-3 text-[11px]">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Both</p>
            {(d.modules_both || []).length === 0 ? <span className="text-muted-foreground">—</span> : (
              <div className="flex flex-wrap gap-1">
                {d.modules_both.map((m) => <Badge key={m} className="text-[9px] bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-mono">{m}</Badge>)}
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Only A</p>
            {(d.modules_only_a || []).length === 0 ? <span className="text-muted-foreground">—</span> : (
              <div className="flex flex-wrap gap-1">
                {d.modules_only_a.map((m) => <Badge key={m} className="text-[9px] bg-muted/30 border border-border/50 font-mono">{m}</Badge>)}
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Only B</p>
            {(d.modules_only_b || []).length === 0 ? <span className="text-muted-foreground">—</span> : (
              <div className="flex flex-wrap gap-1">
                {d.modules_only_b.map((m) => <Badge key={m} className="text-[9px] bg-muted/30 border border-border/50 font-mono">{m}</Badge>)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
